import type {
  EstadoPortaOficina,
  FerramentaOficinaCorte,
  IdZonaOperacaoPorta,
  OperacaoPorta,
} from './oficinaCorte'
import type { EstadoTampaOficina, IdZonaOperacaoTampa, OperacaoTampa } from './oficinaPortaMalas'
import type {
  EstadoTerceiraPortaOficina,
  IdZonaOperacaoTerceiraPorta,
  OperacaoTerceiraPorta,
} from './oficinaTerceiraPorta'

export function chaveProgressoIntervencao(
  operacao: OperacaoPorta | OperacaoTampa | OperacaoTerceiraPorta,
  zona: IdZonaOperacaoPorta | IdZonaOperacaoTampa | IdZonaOperacaoTerceiraPorta | null,
): string {
  return `${operacao}:${zona ?? 'sem-alvo'}`
}

type IntencaoInteracao<TOperacao extends string> =
  | { disponivel: true; operacao: TOperacao; descricao: string }
  | { disponivel: false; operacao: null; descricao: string }

export type IntencaoInteracaoTampa = IntencaoInteracao<OperacaoTampa>
export type IntencaoInteracaoTerceiraPorta = IntencaoInteracao<OperacaoTerceiraPorta>

export type ModoAlargador = 'abrir' | 'fechar'

export type IntencaoInteracaoPorta = IntencaoInteracao<OperacaoPorta>

interface EntradaInteracaoPorta {
  ferramenta: FerramentaOficinaCorte
  zona: IdZonaOperacaoPorta
  modoAlargador: ModoAlargador
  estado: EstadoPortaOficina
  portaDianteira: boolean
}

const indisponivel = <TOperacao extends string>(descricao: string): IntencaoInteracao<TOperacao> => ({
  disponivel: false,
  operacao: null,
  descricao,
})

const disponivel = <TOperacao extends string>(operacao: TOperacao, descricao: string): IntencaoInteracao<TOperacao> => ({
  disponivel: true,
  operacao,
  descricao,
})

/**
 * Traduz o gesto sobre a peça em uma ação de domínio. A cena informa apenas a
 * ferramenta equipada e a região atingida; compatibilidade e consequência
 * continuam sendo validadas pelo motor de `oficinaCorte` ao executar a ação.
 */
export function resolverInteracaoPorta({
  ferramenta,
  zona,
  modoAlargador,
  estado,
  portaDianteira,
}: EntradaInteracaoPorta): IntencaoInteracaoPorta {
  if (estado.removida) return indisponivel('A porta já foi retirada.')

  if (ferramenta === 'cortadorHidraulico') {
    if (zona === 'dobradicaSuperior') return disponivel('cortarDobradiçaSuperior', 'Cortar dobradiça superior')
    if (zona === 'dobradicaInferior') return disponivel('cortarDobradiçaInferior', 'Cortar dobradiça inferior')
    if (zona === 'limitador') return disponivel('cortarLimitador', 'Cortar limitador')
    if (zona === 'fiacao') return disponivel('cortarFiacao', 'Liberar fiação')
    return indisponivel('O cortador precisa ser aplicado em uma dobradiça, no limitador ou na fiação.')
  }

  if (ferramenta === 'hooligan') {
    if (zona === 'chapa' || zona === 'trava') return disponivel('criarAbertura', 'Criar abertura inicial')
    return indisponivel('Posicione o Hooligan na fresta da porta ou junto à trava.')
  }

  if (ferramenta === 'alargador') {
    if (modoAlargador === 'fechar') {
      if (zona === 'macaneta') {
        return estado.tecnicaCriacaoEspaco === 'compressaoMacaneta'
          ? indisponivel('O acesso pela maçaneta já foi criado; abra o alargador no pino da trava.')
          : disponivel('comprimirMacaneta', 'Comprimir a maçaneta e criar acesso')
      }
      if (zona === 'paralama') {
        return portaDianteira
          ? disponivel('amassarParalama', 'Morder o para-lama e criar acesso')
          : indisponivel('O acesso pelo para-lama está disponível junto à porta dianteira.')
      }
      if (zona === 'chapa') {
        return estado.chapa === 'integra'
          ? disponivel('pincarPorta', 'Pinçar a chapa e criar espaço')
          : indisponivel('A chapa já foi deformada; abra o alargador para ampliar o espaço.')
      }
      return indisponivel('No modo fechar, aplique o alargador na maçaneta, na chapa ou no para-lama dianteiro.')
    }

    if (zona === 'trava') return disponivel('expandirTrava', 'Expandir no pino da trava')
    if (zona === 'dobradicaSuperior') return disponivel('expandirDobradiçaSuperior', 'Expandir dobradiça superior')
    if (zona === 'dobradicaInferior') return disponivel('expandirDobradiçaInferior', 'Expandir dobradiça inferior')
    if (zona === 'esquadria') return disponivel('abrirPelaJanela', 'Criar espaço pela esquadria')
    if (zona === 'chapa') {
      return estado.abertura >= 0.1
        ? disponivel('desfolharPorta', 'Desfolhar a chapa externa')
        : indisponivel('Crie uma abertura inicial antes de abrir o alargador na chapa.')
    }
    return indisponivel('No modo abrir, posicione o alargador num vínculo ou numa abertura existente.')
  }

  if (ferramenta === 'extensor') {
    return indisponivel('O extensor trabalha entre dois apoios: escolha a base na soleira e o ponto no painel, não um ponto da porta.')
  }

  return indisponivel('Este equipamento é usado na estabilização, fora da intervenção direta na porta.')
}

/** Resolve a ação diretamente no alvo da tampa, sem depender do menu de operações. */
export function resolverInteracaoTampa({
  ferramenta,
  zona,
  estado,
}: {
  ferramenta: FerramentaOficinaCorte
  zona: IdZonaOperacaoTampa
  estado: EstadoTampaOficina
}): IntencaoInteracaoTampa {
  if (estado.removida) return indisponivel('A tampa já foi retirada.')

  if (ferramenta === 'alargador') {
    if (zona === 'chapa' && estado.abertura < 0.1) return disponivel('criarAbertura', 'Criar abertura na fresta da tampa')
    if (zona === 'trava') return disponivel('expandirTrava', 'Expandir no pino da trava')
    if (zona === 'dobradicaEsquerda') return disponivel('expandirDobradicaEsquerda', 'Expandir dobradiça esquerda')
    if (zona === 'dobradicaDireita') return disponivel('expandirDobradicaDireita', 'Expandir dobradiça direita')
    return indisponivel('Aplique o alargador na fresta, na trava ou em uma dobradiça.')
  }

  if (ferramenta === 'hooligan') {
    return zona === 'chapa' || zona === 'trava'
      ? disponivel('criarAbertura', 'Criar abertura inicial com Hooligan')
      : indisponivel('Apoie o Hooligan na fresta da tampa.')
  }

  if (ferramenta === 'cortadorHidraulico') {
    if (zona === 'dobradicaEsquerda') return disponivel('cortarDobradicaEsquerda', 'Cortar dobradiça esquerda')
    if (zona === 'dobradicaDireita') return disponivel('cortarDobradicaDireita', 'Cortar dobradiça direita')
    if (zona === 'apoio') return disponivel('liberarApoio', 'Liberar apoio a gás')
    return indisponivel('O cortador atua nas dobradiças ou no apoio a gás.')
  }

  return indisponivel('Esta ferramenta não atua nesse ponto da tampa.')
}

/** Resolve o corte da terceira porta pelo lado atingido na própria chapa. */
export function resolverInteracaoTerceiraPorta({
  ferramenta,
  zona,
  estado,
}: {
  ferramenta: FerramentaOficinaCorte
  zona: IdZonaOperacaoTerceiraPorta
  estado: EstadoTerceiraPortaOficina
}): IntencaoInteracaoTerceiraPorta {
  if (estado.removida) return indisponivel('A aba já foi retirada.')
  if (ferramenta !== 'cortadorHidraulico') return indisponivel('Equipe o cortador hidráulico para cortar esta chapa.')
  if (estado.lados[zona] === 'cortado') return indisponivel('Este lado já foi cortado.')
  const operacoes: Record<IdZonaOperacaoTerceiraPorta, OperacaoTerceiraPorta> = {
    superior: 'cortarLadoSuperior',
    inferior: 'cortarLadoInferior',
    traseiro: 'cortarLadoTraseiro',
  }
  return disponivel(operacoes[zona], `Cortar lado ${zona}`)
}
